import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import EventIcon from '@material-ui/icons/Event';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import VideocamOutlinedIcon from '@material-ui/icons/VideocamOutlined';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getUserAvatarUrl } from '../../utils/userAvatar';
import {
  appointmentLocationText,
  resolveAppointmentLocation,
} from '../../utils/appointmentLocation';
import { APPOINTMENT_STATUS } from '../../utils/appointmentStatus';
import { formatAppointmentCountdown } from '../../utils/appointmentCountdown';

const useStyles = makeStyles((theme) => ({
  paper: { borderRadius: 16, width: 460, maxWidth: '95vw' },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: theme.spacing(2.5, 2.5, 1.5),
  },
  headerLeft: { display: 'flex', alignItems: 'flex-start', gap: theme.spacing(1.5), minWidth: 0 },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#16a34a',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerTitleBox: { minWidth: 0, paddingTop: 2 },
  title: { fontWeight: 700, color: '#0f172a', fontSize: '1rem', wordBreak: 'break-word' },
  statusChip: { fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: 6, marginTop: 6 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  countdownChip: {
    backgroundColor: '#fef3c7',
    color: '#b45309',
    fontWeight: 700,
    fontSize: '0.75rem',
    height: 24,
    borderRadius: 8,
  },
  closeButton: { padding: 6, color: '#94a3b8' },

  content: { padding: theme.spacing(0, 2.5, 2.5) },
  divider: { borderTop: '1px solid #e8edf2', margin: theme.spacing(1.5, 0) },

  whenRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  whenItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#0f172a',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  whenIcon: { fontSize: 18, color: '#16a34a' },
  durationHint: { fontSize: '0.75rem', color: '#94a3b8', textAlign: 'right', marginTop: 2 },

  sectionLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#94a3b8',
    marginBottom: theme.spacing(1),
  },

  participantsRow: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.25) },
  avatarStack: { display: 'flex', alignItems: 'center' },
  avatar: { width: 32, height: 32, fontSize: '0.8rem', border: '2px solid #fff' },
  avatarOverlap: { marginLeft: -10 },
  participantName: { fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' },
  participantSub: { fontSize: '0.75rem', color: '#64748b' },

  meetRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  meetLeft: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  meetLabel: { fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' },
  meetButton: {
    backgroundColor: '#16a34a',
    color: '#fff',
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#15803d', boxShadow: 'none' },
  },

  moreToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    color: '#475569',
    marginTop: theme.spacing(2),
    userSelect: 'none',
  },
  moreToggleLabel: { fontSize: '0.8125rem', fontWeight: 600 },
  moreChevron: { fontSize: 20, transition: 'transform 0.15s ease' },
  moreChevronOpen: { transform: 'rotate(180deg)' },
  moreContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    paddingTop: theme.spacing(1.5),
  },
  field: { display: 'flex', flexDirection: 'column', gap: 2 },
  fieldLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  fieldValue: { fontSize: '0.8125rem', color: '#334155' },

  actions: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2.5),
    borderTop: '1px solid #e8edf2',
  },
  viewAppointmentsButton: {
    textTransform: 'none',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
}));

/**
 * Visão rápida de um compromisso — versão compacta do `AppointmentDetailsModal`,
 * pensada pro clique no aviso da barra superior (`NextMeetingChip`): o essencial
 * (quando, com quem, link da reunião) na frente, o resto atrás de "Mais detalhes".
 */
export default function AppointmentQuickViewModal({
  open,
  onClose,
  appointment,
  onViewAppointments,
}) {
  const classes = useStyles();
  const [now, setNow] = useState(() => new Date());
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, [open]);

  useEffect(() => {
    setMoreOpen(false);
  }, [appointment]);

  if (!appointment) return null;

  const status = APPOINTMENT_STATUS[appointment.status] || APPOINTMENT_STATUS.scheduled;
  const location = resolveAppointmentLocation(appointment, appointment.calendar);
  const start = parseISO(appointment.startDatetime);
  const end = new Date(start.getTime() + (appointment.durationMinutes || 0) * 60000);
  const countdown = formatAppointmentCountdown(start, now);

  const guestLabel = appointment.guestName || appointment.contact?.name || appointment.client?.name;
  const { host } = appointment;
  const canViewAppointments = Boolean(onViewAppointments);

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Box className={classes.headerIcon}>
            <EventAvailableIcon />
          </Box>
          <Box className={classes.headerTitleBox}>
            <Typography className={classes.title}>{appointment.title}</Typography>
            <Chip
              size="small"
              label={status.label}
              className={classes.statusChip}
              style={{ backgroundColor: status.bg, color: status.fg }}
            />
          </Box>
        </Box>
        <Box className={classes.headerRight}>
          <Chip size="small" label={countdown} className={classes.countdownChip} />
          <IconButton onClick={onClose} className={classes.closeButton} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box className={classes.content}>
        <Box className={classes.divider} />

        <Box className={classes.whenRow}>
          <Box className={classes.whenItem}>
            <EventIcon className={classes.whenIcon} />
            <span style={{ textTransform: 'capitalize' }}>
              {format(start, "EEEE, d 'de' MMMM", { locale: ptBR })}
            </span>
          </Box>
          <Box>
            <Box className={classes.whenItem}>
              <AccessTimeIcon className={classes.whenIcon} />
              {format(start, 'HH:mm')}–{format(end, 'HH:mm')}
            </Box>
            <Typography className={classes.durationHint}>
              {appointment.durationMinutes} minutos
            </Typography>
          </Box>
        </Box>

        <Box className={classes.divider} />

        <Typography className={classes.sectionLabel}>Participantes</Typography>
        <Box className={classes.participantsRow}>
          <Box className={classes.avatarStack}>
            {guestLabel && (
              <Avatar className={classes.avatar}>{guestLabel[0]?.toUpperCase()}</Avatar>
            )}
            {host && (
              <Avatar
                src={getUserAvatarUrl(host.profileImage, appointment.companyId) || undefined}
                className={`${classes.avatar} ${guestLabel ? classes.avatarOverlap : ''}`}
              >
                {host.name?.[0]?.toUpperCase()}
              </Avatar>
            )}
          </Box>
          <Box>
            <Typography className={classes.participantName}>
              {guestLabel || host?.name || 'Sem responsável'}
            </Typography>
            {host && (
              <Typography className={classes.participantSub}>
                {guestLabel ? `${host.name} · Responsável` : 'Responsável'}
              </Typography>
            )}
          </Box>
        </Box>

        {location.type === 'google_meet' ? (
          <Box className={classes.meetRow}>
            <Box className={classes.meetLeft}>
              <VideocamOutlinedIcon style={{ color: '#16a34a' }} />
              <Typography className={classes.meetLabel}>Google Meet</Typography>
            </Box>
            {appointment.meetLink ? (
              <Button
                variant="contained"
                className={classes.meetButton}
                href={appointment.meetLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Entrar na reunião
              </Button>
            ) : (
              <Typography className={classes.participantSub}>Link ainda não gerado</Typography>
            )}
          </Box>
        ) : (
          location.type && (
            <Box className={classes.meetRow}>
              <Typography className={classes.participantSub}>
                {appointmentLocationText(location.type, location.details)}
              </Typography>
            </Box>
          )
        )}

        <Box className={classes.moreToggle} onClick={() => setMoreOpen((v) => !v)}>
          <Typography className={classes.moreToggleLabel}>Mais detalhes</Typography>
          <ExpandMoreIcon
            className={`${classes.moreChevron} ${moreOpen ? classes.moreChevronOpen : ''}`}
          />
        </Box>
        <Collapse in={moreOpen}>
          <Box className={classes.moreContent}>
            <Box className={classes.field}>
              <Typography className={classes.fieldLabel}>Calendário</Typography>
              <Typography className={classes.fieldValue}>
                {appointment.calendar?.name || '—'}
              </Typography>
            </Box>
            <Box className={classes.field}>
              <Typography className={classes.fieldLabel}>Fuso do convidado</Typography>
              <Typography className={classes.fieldValue}>{appointment.timezone || '—'}</Typography>
            </Box>
            {appointment.description && (
              <Box className={classes.field}>
                <Typography className={classes.fieldLabel}>Observações</Typography>
                <Typography className={classes.fieldValue}>{appointment.description}</Typography>
              </Box>
            )}
            {appointment.guests?.length > 0 && (
              <Box className={classes.field}>
                <Typography className={classes.fieldLabel}>
                  Convidados ({appointment.guests.length})
                </Typography>
                <Typography className={classes.fieldValue}>
                  {appointment.guests.map((g) => g.name || g.email).join(', ')}
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>

      <Box
        className={classes.actions}
        style={{ justifyContent: canViewAppointments ? 'space-between' : 'flex-end' }}
      >
        {canViewAppointments && (
          <Button
            startIcon={<ArrowForwardIcon />}
            className={classes.viewAppointmentsButton}
            onClick={() => onViewAppointments(appointment)}
          >
            Meus compromissos
          </Button>
        )}
        <Button variant="outlined" onClick={onClose}>
          Fechar
        </Button>
      </Box>
    </Dialog>
  );
}

AppointmentQuickViewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  appointment: PropTypes.object,
  onViewAppointments: PropTypes.func,
};

AppointmentQuickViewModal.defaultProps = {
  appointment: null,
  onViewAppointments: undefined,
};
